import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BranchProvider } from "@/contexts/BranchContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import TwoFactorPanel from "@/components/TwoFactorPanel";
import { ShieldCheck } from "lucide-react";

import Login from "@/pages/Login";
import SuperAdmin from "@/pages/SuperAdmin";
import Dashboard from "@/pages/Dashboard";
import Bookings from "@/pages/Bookings";
import Leads from "@/pages/Leads";
import PerformanceReports from "@/pages/PerformanceReports";
import SalesReview from "@/pages/SalesReview";
import Patients from "@/pages/Patients";
import Initiatives from "@/pages/Initiatives";
import Loyalty from "@/pages/Loyalty";
import Notifications from "@/pages/Notifications";
import Finance from "@/pages/Finance";
import Accounting from "@/pages/Accounting";
import Security from "@/pages/Security";
import UserManagement from "@/pages/UserManagement";
import Inventory from "@/pages/Inventory";
import Promos from "@/pages/Promos";
import Transactions from "@/pages/Transactions";
import TransactionHistory from "@/pages/TransactionHistory";
import MedicalRecords from "@/pages/MedicalRecords";
import Commissions from "@/pages/Commissions";
import PromoAnalytics from "@/pages/PromoAnalytics";
import Settings from "@/pages/Settings";
import Migration from "@/pages/Migration";
import Consultation from "@/pages/Consultation";
import Registration from "@/pages/Registration";
import ConsolidatedDashboard from "@/pages/ConsolidatedDashboard";
import Reports from "@/pages/Reports";
import LinkHome from "@/pages/linkbio/LinkHome";
import LinkSolusi from "@/pages/linkbio/LinkSolusi";
import LinkEstimasi from "@/pages/linkbio/LinkEstimasi";

import { SettingsProvider } from "@/contexts/SettingsContext";

const DEFAULT_HOME_BY_ROLE = {
  super_admin: "/admin",
  owner: "/",
  manager: "/",
  fo_cs: "/bookings",
  cs: "/performance",
  crm: "/performance",
  doctor: "/bookings",
  therapist: "/bookings",
};

function RoleGate({ allow, children, fallback = "/bookings" }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "super_admin") return children; // super admin has full access
  if (!allow.includes(user.role)) return <Navigate to={fallback} replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  const dest = DEFAULT_HOME_BY_ROLE[user?.role] || "/bookings";
  if (dest === "/") return <Dashboard />;
  return <Navigate to={dest} replace />;
}

function AdminOnly({ children }) {
  const { user, refreshUser, logout } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/" replace />;
  if (user.mfa_setup_required) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="force-2fa-gate">
        <div className="w-full max-w-lg space-y-4">
          <div className="text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-foreground text-background">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h1 className="font-heading text-2xl tracking-tight">Aktifkan 2FA untuk Melanjutkan</h1>
            <p className="mt-1 text-sm text-muted-foreground">Super Admin wajib mengaktifkan autentikasi dua faktor.</p>
          </div>
          <TwoFactorPanel mandatory onEnrolled={refreshUser} />
          <button onClick={logout} className="block w-full text-center text-xs text-muted-foreground hover:text-foreground">Keluar</button>
        </div>
      </div>
    );
  }
  return children;
}

function NonAdminLayout() {
  return (
    <SettingsProvider>
      <BranchProvider>
        <Layout />
      </BranchProvider>
    </SettingsProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/link" element={<LinkHome />} />
            <Route path="/link/solusi" element={<LinkSolusi />} />
            <Route path="/link/estimasi" element={<LinkEstimasi />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route
              path="/admin"
              element={
                <AdminOnly>
                  <SuperAdmin />
                </AdminOnly>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <NonAdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<HomeRedirect />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="leads" element={<RoleGate allow={["cs","manager","owner","super_admin"]}><Leads /></RoleGate>} />
              <Route path="performance" element={<RoleGate allow={["cs","crm","manager","owner","super_admin"]}><PerformanceReports /></RoleGate>} />
              <Route path="sales-review" element={<RoleGate allow={["crm","manager","owner","super_admin"]}><SalesReview /></RoleGate>} />
              <Route path="consultation" element={<RoleGate allow={["owner","manager","fo_cs","doctor"]}><Consultation /></RoleGate>} />
              <Route path="registration" element={<RoleGate allow={["owner","manager","fo_cs","doctor"]}><Registration /></RoleGate>} />
              <Route path="patients" element={<Patients />} />
              <Route path="initiatives" element={<RoleGate allow={["owner","manager"]}><Initiatives /></RoleGate>} />
              <Route path="loyalty" element={<RoleGate allow={["owner","manager","fo_cs"]}><Loyalty /></RoleGate>} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="finance" element={<RoleGate allow={["owner"]}><Finance /></RoleGate>} />
              <Route path="accounting" element={<RoleGate allow={["owner"]}><Accounting /></RoleGate>} />
              <Route path="inventory" element={<RoleGate allow={["owner","manager","therapist","fo_cs"]}><Inventory /></RoleGate>} />
              <Route path="promos" element={<RoleGate allow={["owner","manager","fo_cs"]}><Promos /></RoleGate>} />
              <Route path="transactions" element={<RoleGate allow={["owner","manager","fo_cs"]}><Transactions /></RoleGate>} />
              <Route path="transaction-history" element={<RoleGate allow={["owner","manager","fo_cs"]}><TransactionHistory /></RoleGate>} />
              <Route path="consolidated" element={<RoleGate allow={["owner"]}><ConsolidatedDashboard /></RoleGate>} />
              <Route path="reports" element={<RoleGate allow={["owner","manager","fo_cs"]}><Reports /></RoleGate>} />
              <Route path="medical-records" element={<RoleGate allow={["owner","manager","doctor"]}><MedicalRecords /></RoleGate>} />
              <Route path="commissions" element={<RoleGate allow={["owner","manager","doctor","therapist"]}><Commissions /></RoleGate>} />
              <Route path="promo-analytics" element={<RoleGate allow={["owner","manager"]}><PromoAnalytics /></RoleGate>} />
              <Route path="settings" element={<RoleGate allow={["owner","manager"]}><Settings /></RoleGate>} />
              <Route path="migration" element={<RoleGate allow={["owner","manager","fo_cs"]}><Migration /></RoleGate>} />
              <Route path="security" element={<Security />} />
              <Route path="users" element={<RoleGate allow={["owner","manager"]}><UserManagement /></RoleGate>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
